import { Fragment, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  VENDOR_STATUSES,
  balanceCents,
  computeVendorStats,
  groupByCategory,
  nextDue,
  paidCents,
  sortVendors,
  spendByCategory,
  upcomingPayments,
  type Vendor,
  type VendorStatus,
  type Wedding,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { FilterPill } from '@/components/ui/FilterPill';
import { Pill } from '@/components/ui/Pill';
import { Metric, MetricFootnote } from '@/components/ui/Stat';
import { TextInput } from '@/components/ui/Field';
import { money } from '@/lib/format';
import { VendorDrawer } from './VendorDrawer';
import { useVendors } from './queries';

/**
 * Every supplier, grouped by the kind of thing they do.
 *
 * The question this tab answers is **what is still to pay and when**, not what
 * things cost — the quote stops being the interesting number the moment the
 * first deposit goes out. So the two headline figures are what is outstanding
 * and what is overdue, and the table leads with the balance.
 *
 * Categories collapse the way households do on the guest list, and start closed,
 * because "eleven suppliers" reads better as six kinds of supplier.
 */

const toneFor = (status: VendorStatus) =>
  status === 'paid'
    ? 'attending'
    : status === 'booked'
      ? 'booked'
      : status === 'quoted'
        ? 'pending'
        : 'neutral';

export function VendorsTab() {
  const { t } = useTranslation();
  const { wedding, canWrite } = useOutletContext<{ wedding: Wedding; canWrite: boolean }>();
  const wedId = wedding.wedId;
  const currency = wedding.settings.currency;

  const vendors = useVendors(wedId);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<VendorStatus | 'all'>('all');
  const [editing, setEditing] = useState<Vendor | undefined>();
  const [adding, setAdding] = useState(false);
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());

  const all = vendors.data?.items ?? [];
  const stats = vendors.data?.stats ?? computeVendorStats(all);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((v) => statusFilter === 'all' || v.status === statusFilter)
      .filter(
        (v) =>
          !q ||
          v.name.toLowerCase().includes(q) ||
          (v.contactName ?? '').toLowerCase().includes(q) ||
          t(`vendorCategories.${v.category ?? 'other'}`)
            .toLowerCase()
            .includes(q),
      );
  }, [all, query, statusFilter, t]);

  const groups = useMemo(() => groupByCategory(sortVendors(shown, 'due')), [shown]);
  const categories = useMemo(() => spendByCategory(all), [all]);
  const due = useMemo(() => upcomingPayments(all, { withinDays: 60 }), [all]);

  // A search or a filter has already decided these are the interesting rows.
  const forceOpen = query.trim() !== '' || statusFilter !== 'all';
  const allOpen = groups.length > 0 && groups.every((g) => openCategories.has(g.category));

  const toggle = (category: string) =>
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });

  const openVendor = editing ? all.find((v) => v.vendorId === editing.vendorId) : undefined;

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">{t('nav.vendors')}</p>
          <h1 className="font-display text-display">{t('vendors.heading')}</h1>
        </div>
        {canWrite && <Button onClick={() => setAdding(true)}>{t('vendors.add')}</Button>}
      </div>

      <div className="panel flex flex-col gap-5 p-5">
        <div className="flex flex-wrap gap-6">
          {/* First, because it is the biggest number and the one being decided
              on: what the suppliers come to if every price on the board turns
              into a booking. The two beside it are about money already
              committed, which is a narrower and later question. */}
          <Metric
            label={t('vendors.expected')}
            value={money(stats.expectedCents, currency)}
            sub={t('vendors.expectedSub', { count: stats.expectedFrom })}
          />
          <Metric
            label={t('vendors.outstanding')}
            value={money(stats.outstandingCents, currency)}
            sub={t('vendors.outstandingSub')}
          />
          <Metric
            label={t('vendors.paid')}
            value={money(stats.paidCents, currency)}
            sub={t('vendors.paidSub')}
          />
        </div>
        <div className="border-t border-line pt-4">
          <MetricFootnote
            items={[
              t('vendors.bookedCount', { count: stats.booked, total: stats.total }),
              stats.overdue > 0 ? t('vendors.overdueCount', { count: stats.overdue }) : undefined,
              stats.undatedCents > 0
                ? t('vendors.undated', { amount: money(stats.undatedCents, currency) })
                : undefined,
            ]}
          />
        </div>
      </div>

      {/* What needs doing, before anything about what things cost. */}
      {due.length > 0 && (
        <div className="panel flex flex-col gap-2 p-4">
          <span className="eyebrow">{t('vendors.comingUp')}</span>
          <ul className="flex flex-col divide-y divide-line">
            {due.slice(0, 6).map((d) => (
              <li
                key={d.payment.paymentId}
                className="flex flex-wrap items-baseline justify-between gap-2 py-2"
              >
                <span className="flex flex-wrap items-baseline gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(all.find((v) => v.vendorId === d.vendorId))}
                    className="text-body underline underline-offset-4"
                  >
                    {d.vendorName}
                  </button>
                  <span className="text-caption text-leaf-soft">{d.payment.label}</span>
                </span>
                <span className="flex items-baseline gap-3">
                  <span className="tabular text-body">
                    {money(d.payment.amountCents, d.currency)}
                  </span>
                  {d.overdue ? (
                    <Pill tone="declined">
                      {t('vendors.overdueBy', { count: Math.abs(d.inDays) })}
                    </Pill>
                  ) : (
                    <span className="tabular text-caption text-leaf-soft">
                      {d.inDays === 0
                        ? t('vendors.dueToday')
                        : t('vendors.inDays', { count: d.inDays })}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex-1">
          <span className="sr-only">{t('vendors.search')}</span>
          <TextInput
            type="search"
            placeholder={t('vendors.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <FilterPill
            label={t('vendors.all')}
            count={stats.total}
            selected={statusFilter === 'all'}
            onSelect={() => setStatusFilter('all')}
          />
          {VENDOR_STATUSES.map((s) => (
            <FilterPill
              key={s}
              label={t(`vendors.statusValue.${s}`)}
              count={stats.byStatus[s]}
              selected={statusFilter === s}
              onSelect={() => setStatusFilter(s)}
            />
          ))}
        </div>
      </div>

      {vendors.isLoading && <p className="text-body text-leaf-soft">{t('common.loading')}</p>}
      {vendors.isError && <p className="text-body text-leaf-soft">{t('common.somethingWrong')}</p>}

      {vendors.data && groups.length === 0 && (
        <EmptyState
          title={all.length === 0 ? t('empty.vendorsTitle') : t('vendors.noMatchTitle')}
          body={all.length === 0 ? t('empty.vendorsBody') : t('vendors.noMatchBody')}
          action={
            all.length === 0 && canWrite ? (
              <Button onClick={() => setAdding(true)}>{t('vendors.add')}</Button>
            ) : undefined
          }
        />
      )}

      {!forceOpen && groups.length > 0 && (
        <div className="-mb-2 flex justify-end">
          <button
            type="button"
            onClick={() =>
              setOpenCategories(allOpen ? new Set() : new Set(groups.map((g) => g.category)))
            }
            className="min-h-tap text-caption text-leaf-soft underline underline-offset-4 hover:text-leaf"
          >
            {allOpen ? t('guests.collapseAll') : t('guests.expandAll')}
          </button>
        </div>
      )}

      {groups.length > 0 && (
        <div className="panel overflow-x-auto">
          {/*
            Seven columns, and only two of them answer the question this tab is
            for. Below `md` the balance keeps its column and the rest — status,
            quote, paid, next due — move under the name, where they read as a
            line of detail rather than as a grid nobody can see the right-hand
            end of.
          */}
          <table className="w-full border-collapse text-body">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-3 py-3 text-caption font-medium sm:px-4">{t('vendors.name')}</th>
                <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                  {t('vendors.status')}
                </th>
                <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                  {t('vendors.quote')}
                </th>
                <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                  {t('vendors.paidShort')}
                </th>
                <th className="px-3 py-3 text-caption font-medium sm:px-4">
                  {t('vendors.balance')}
                </th>
                <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                  {t('vendors.nextDue')}
                </th>
                <th className="px-3 py-3 sm:px-4" />
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => {
                const open = forceOpen || openCategories.has(group.category);
                const spend = categories.find((c) => c.category === group.category);
                return (
                  <Fragment key={group.category}>
                    <tr className="border-b border-line bg-cream/40">
                      <td className="px-3 py-2 sm:px-4" colSpan={4}>
                        <span className="flex min-w-0 flex-wrap items-center gap-2">
                          <button
                            type="button"
                            aria-expanded={open}
                            onClick={() => toggle(group.category)}
                            className="-mx-2 inline-flex min-h-tap min-w-0 max-w-full items-center gap-2 rounded px-2 text-left hover:bg-cream"
                          >
                            <svg
                              aria-hidden="true"
                              viewBox="0 0 10 6"
                              className={`h-1.5 w-2.5 shrink-0 text-leaf-soft transition-transform ${
                                open ? '' : '-rotate-90'
                              }`}
                            >
                              <path
                                d="M1 1l4 4 4-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              />
                            </svg>
                            <span className="min-w-0 truncate font-medium">
                              {t(`vendorCategories.${group.category}`)}
                            </span>
                            <Pill>{group.vendors.length}</Pill>
                          </button>
                          {/* Closed, the names are what makes a category
                              reviewable without opening it. */}
                          {!open && (
                            <span className="min-w-0 flex-1 truncate text-caption text-leaf-soft">
                              {group.vendors.map((v) => v.name).join(', ')}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right sm:px-4" colSpan={3}>
                        {spend && spend.outstandingCents > 0 && (
                          <span className="tabular text-caption text-leaf-soft">
                            {t('vendors.categoryOutstanding', {
                              amount: money(spend.outstandingCents, currency),
                            })}
                          </span>
                        )}
                      </td>
                    </tr>

                    {open &&
                      group.vendors.map((v) => {
                        const balance = balanceCents(v);
                        const next = nextDue(v);
                        return (
                          <tr key={v.vendorId} className="border-b border-line last:border-0">
                            <td className="px-3 py-3 pl-5 sm:px-4 sm:pl-8">
                              <span className="flex flex-col">
                                <span>{v.name}</span>
                                {v.contactName && (
                                  <span className="text-caption text-leaf-soft">
                                    {v.contactName}
                                  </span>
                                )}
                                {/* The columns that left the grid below `md`. */}
                                <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-leaf-soft [overflow-wrap:anywhere] md:hidden">
                                  <Pill tone={toneFor(v.status ?? 'researching')}>
                                    {t(`vendors.statusValue.${v.status ?? 'researching'}`)}
                                  </Pill>
                                  {v.quoteCents !== undefined && (
                                    <span className="tabular">
                                      {money(v.quoteCents, v.currency ?? currency)}
                                    </span>
                                  )}
                                  {next && (
                                    <span
                                      className={`tabular ${next.overdue ? 'text-status-declinedFg' : ''}`}
                                    >
                                      {next.payment.dueDate}
                                    </span>
                                  )}
                                </span>
                              </span>
                            </td>
                            <td className="hidden px-4 py-3 md:table-cell">
                              <Pill tone={toneFor(v.status ?? 'researching')}>
                                {t(`vendors.statusValue.${v.status ?? 'researching'}`)}
                              </Pill>
                            </td>
                            <td className="tabular hidden px-4 py-3 text-caption text-leaf-soft md:table-cell">
                              {v.quoteCents === undefined
                                ? '—'
                                : money(v.quoteCents, v.currency ?? currency)}
                            </td>
                            <td className="tabular hidden px-4 py-3 text-caption text-leaf-soft md:table-cell">
                              {money(paidCents(v), v.currency ?? currency)}
                            </td>
                            <td className="tabular px-3 py-3 sm:px-4">
                              {balance === undefined ? '—' : money(balance, v.currency ?? currency)}
                            </td>
                            <td className="hidden px-4 py-3 text-caption md:table-cell">
                              {next ? (
                                <span className="flex min-w-0 flex-wrap items-center gap-2">
                                  <span className="tabular text-leaf-soft">
                                    {next.payment.dueDate}
                                  </span>
                                  {next.overdue && (
                                    <Pill tone="declined">{t('vendors.overdue')}</Pill>
                                  )}
                                </span>
                              ) : (
                                <span className="text-leaf-soft">—</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-right sm:px-4">
                              <button
                                type="button"
                                onClick={() => setEditing(v)}
                                className="min-h-tap text-caption text-brass-text underline underline-offset-4"
                              >
                                {t('vendors.open')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <VendorDrawer
          canWrite={canWrite}
          wedId={wedId}
          currency={currency}
          open
          onClose={() => setAdding(false)}
          onCreated={(v) => setEditing(v)}
        />
      )}

      {openVendor && (
        <VendorDrawer
          canWrite={canWrite}
          key={openVendor.vendorId}
          wedId={wedId}
          vendor={openVendor}
          currency={currency}
          open
          onClose={() => setEditing(undefined)}
        />
      )}
    </section>
  );
}
